import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import colors from "assets/theme-dark/base/colors";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import {
  DriverMode,
  QueryProcessedDataViewsQuery,
  SegmentGroupInput,
  SegmentationMethod as GraphQLSegmentationMethod,
} from "graphql/types/graphql";
import Tooltip from "@mui/material/Tooltip";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { AutoObject, InfiniteAutocomplete } from "./autocomplete/InfiniteAutocomplete";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { context } from "utils/Provider";
import OverwriteProjectDialog from "./OverwriteProjectDialog";
import DeleteProjectDialog from "./DeleteProjectDialog";
import { useHeadroomProjects } from "../hooks/useHeadroomProjects";
import { enqueueSnackbar } from "notistack";
import { useDeleteHeadroomProject } from "../hooks/useDeleteHeadroomProject";
import { cruxyColors } from "../theme/color-palette";

interface HeadroomHeaderProps {
  data?: QueryProcessedDataViewsQuery;
  loading: boolean;
  filteredOptions: string[];
  setSearchTerm: (value: string) => void;
  handleFetchMore: () => void;
}

const HeadroomHeader: React.FC<HeadroomHeaderProps> = ({
  data,
  loading: dataViewsLoading,
  filteredOptions,
  setSearchTerm,
  handleFetchMore,
}) => {
  const {
    tableName,
    setTableName,
    selectedProject,
    setSelectedProject,
    projectNameInput,
    setProjectNameInput,
    segmentGroups,
    driver1Type,
    driver2Type,
    driver1NumeratorType,
    driver2NumeratorType,
    driver1Mode,
    driver2Mode,
    count1Column,
    count2Column,
    customerColumn,
    driver1Percentile,
    driver2Percentile,
    headroomDriver,
    headroomDriverPercentile,
  } = useHeadroomContext();
  const { selectedCube, setIsLoading } = React.useContext(context);
  const { projectOptions, loading, setProjectSearchTerm, handleFetchMoreProjects, saveProject, allFetchedProjects } =
    useHeadroomProjects();
  const { deleteProject, loading: deleteLoading } = useDeleteHeadroomProject();

  const [projectNameError, setProjectNameError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  React.useEffect(() => {
    setIsLoading(loading);
  }, [loading, setIsLoading]);

  if (!selectedCube.cubeId) {
    return (
      <StyledCard>
        <MDTypography variant="body1" color={colors.text.main}>
          Loading cube data...
        </MDTypography>
      </StyledCard>
    );
  }

  const autoOptions: AutoObject[] = filteredOptions?.map((option) => ({
    id: option,
    value: option,
  }));

  const isMoreToFetch = data?.queryProcessedDataViews?.hasNextPage || false;

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleClear = () => {
    setTableName(null);
    setSearchTerm("");
  };

  const handleProjectNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setProjectNameInput(name);
  };

  const mapSegmentGroupsToInput = (groups: typeof segmentGroups): SegmentGroupInput[] => {
    return groups?.map((group) => ({
      index: group.index,
      type: group.type,
      method: group.method as GraphQLSegmentationMethod,
      segments: group.segments?.map((segment) => ({
        id: segment.id,
        minValue: segment.minValue ?? null,
        maxValue: segment.maxValue ?? null,
      })),
      rangeColumn: group.rangeColumn,
    }));
  };

  const handleSaveProject = async (overwrite: boolean = false) => {
    if (!tableName || !projectNameInput) {
      setProjectNameError("Project name and table name are required");
      return;
    }

    if (!overwrite && allFetchedProjects.includes(projectNameInput)) {
      setIsDialogOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      await saveProject({
        projectName: projectNameInput,
        tableName,
        cubeId: selectedCube.cubeId,
        segmentGroups: mapSegmentGroupsToInput(segmentGroups),
        driver1Type: driver1Type || null,
        driver2Type: driver2Type || null,
        driver1NumeratorType: driver1NumeratorType || null,
        driver2NumeratorType: driver2NumeratorType || null,
        driver1Mode: (driver1Mode as DriverMode) || null,
        driver2Mode: (driver2Mode as DriverMode) || null,
        count1Column: count1Column || null,
        count2Column: count2Column || null,
        customerColumn: customerColumn || null,
        driver1Percentile: driver1Percentile || null,
        driver2Percentile: driver2Percentile || null,
        headroomDriver: headroomDriver || null,
        headroomPercentile: headroomDriverPercentile || null,
      });
      setSelectedProject(projectNameInput);
      setProjectNameInput("");
    } catch (error) {
      enqueueSnackbar("Failed to save project", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverwriteConfirm = async () => {
    setIsDialogOpen(false);
    await handleSaveProject(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleDeleteProject = async () => {
    if (selectedProject) {
      setIsDeleteDialogOpen(false);
      try {
        setIsLoading(true);
        await deleteProject(selectedProject, handleFetchMoreProjects);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteDialogClose = () => {
    setIsDeleteDialogOpen(false);
  };

  const isSaveDisabled = !projectNameInput || !!projectNameError || !tableName;
  const isDeleteDisabled = !selectedProject || deleteLoading;

  return (
    <StyledCard>
      <MDBox display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <MDBox display="flex" alignItems="center" flexGrow={1} maxWidth="calc(100% - 150px)">
          <MDTypography variant="h5" fontWeight="bold" mr={12}>
            Headroom
          </MDTypography>
          <MDBox display="flex" alignItems="center" flexGrow={1} maxWidth="600px">
            <MDBox width="50%" mr={2}>
              <Autocomplete
                options={projectOptions?.map((p) => p.name) || []}
                value={selectedProject}
                onChange={(_event, newValue) => setSelectedProject(newValue)}
                onInputChange={(_event, newInputValue) => setProjectSearchTerm(newInputValue)}
                renderInput={(params) => (
                  <StyledTextField {...params} label="Select Project" variant="outlined" size="small" />
                )}
                size="small"
                loading={loading}
              />
            </MDBox>
            <MDBox width="50%">
              <InfiniteAutocomplete
                data={autoOptions}
                label="Select Data"
                loading={dataViewsLoading}
                isMoreToFetch={isMoreToFetch}
                fetchMore={handleFetchMore}
                onChange={(event, newValue) => {
                  setTableName(newValue);
                }}
                onClear={handleClear}
                searchBy={handleSearch}
                small
                controllState={{
                  state: tableName ? { id: tableName, value: tableName } : { id: "", value: "" },
                  setState: (value: AutoObject) => setTableName(value.value || null),
                }}
              />
            </MDBox>
          </MDBox>
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDBox width="200px">
            <StyledTextField
              value={projectNameInput}
              onChange={handleProjectNameChange}
              label="Enter Project Name"
              variant="outlined"
              size="small"
              error={!!projectNameError}
              helperText={projectNameError}
            />
          </MDBox>
          <Tooltip title="Save Project" arrow placement="left-end">
            <span>
              <IconButton
                onClick={() => handleSaveProject()}
                disabled={isSaveDisabled}
                sx={{
                  color: colors.grey[900],
                  "&:hover": {
                    color: colors.info.focus,
                  },
                  "&:disabled": {
                    color: colors.grey[500],
                  },
                }}
              >
                <SaveIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete Project" arrow placement="left-end">
            <span>
              <IconButton
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={isDeleteDisabled}
                sx={{
                  color: cruxyColors.red.main,
                  "&:hover": {
                    color: colors.info.focus,
                  },
                  "&:disabled": {
                    color: colors.grey[500],
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </MDBox>
      </MDBox>
      <OverwriteProjectDialog
        open={isDialogOpen}
        projectName={projectNameInput}
        onClose={handleDialogClose}
        onOverwrite={handleOverwriteConfirm}
        overwriteLoading={loading}
      />
      <DeleteProjectDialog
        open={isDeleteDialogOpen}
        projectName={selectedProject}
        onClose={handleDeleteDialogClose}
        onDelete={handleDeleteProject}
        deleteLoading={deleteLoading}
      />
    </StyledCard>
  );
};

const StyledCard = styled(MDBox)(({ theme }) => ({
  marginTop: "1px",
  background: `linear-gradient(145deg, ${colors.background.card} 0%, ${colors.background.default} 100%)`,
  borderRadius: theme.spacing(1.5),
  padding: "10px",
  boxShadow: theme.shadows[4],
  transition: "all 0.3s ease",
  width: "100%",
  marginBottom: theme.spacing(1.5),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: colors.text.main },
    "&:hover fieldset": { borderColor: colors.primary.main },
    "&.Mui-focused fieldset": { borderColor: colors.primary.main },
    color: colors.text.main,
  },
  "& .MuiInputBase-input": { color: colors.text.main },
  "& .MuiInputLabel-root": { color: colors.grey[900] },
  "& .MuiInputLabel-root.Mui-focused": { color: colors.grey[500] },
  "& .MuiInputLabel-root.Mui-disabled": { color: colors.grey[500] },
  "& .MuiFormHelperText-root": {
    color: colors.error.main,
  },
}));

export default HeadroomHeader;
